import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { SwalAlert } from '@/hooks/useSweetAlert';
import { defaultCommunityFeatures, type CommunityFeatureItem } from '@/mocks/adminCommunityConfig';

type SaveStatus = 'idle' | 'saving' | 'saved';

export default function AdminCommunityConfigPage() {
  const [features, setFeatures] = useState<CommunityFeatureItem[]>(defaultCommunityFeatures);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');

  const toggleFeature = (id: string) => {
    setFeatures((prev) =>
      prev.map((f) => (f.id === id ? { ...f, enabled: !f.enabled } : f)),
    );
  };

  const handleSave = useCallback(() => {
    setSaveStatus('saving');
    setTimeout(() => {
      setSaveStatus('saved');
      SwalAlert.toast('Configuración guardada correctamente', 'success');
      setTimeout(() => setSaveStatus('idle'), 1500);
    }, 600);
  }, []);

  const activeCount = features.filter((f) => f.enabled).length;
  const totalCount = features.length;

  return (
    <div className="min-h-screen bg-background-100">
      {/* Mobile topbar */}
      <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-accent-500 flex items-center justify-center">
              <i className="ri-group-line text-background-50 text-xs"></i>
            </div>
            <div>
              <h1 className="text-foreground-950 font-bold text-sm">Comunidad</h1>
              <p className="text-foreground-400 text-[9px]">Configuración</p>
            </div>
          </div>
          <Link
            to="/admin/community"
            className="flex items-center gap-1.5 text-foreground-500 text-xs hover:text-foreground-700 transition-colors"
          >
            <i className="ri-arrow-left-line text-xs"></i>
            Comunidad
          </Link>
        </div>
      </header>

      <div className="flex">


        <main className="flex-1 min-w-0">
          {/* Desktop toolbar */}
          <div className="hidden lg:flex items-center justify-between h-16 px-8 border-b border-background-200 bg-background-50 sticky top-0 z-20">
            <div className="flex items-center gap-4">
              <Link
                to="/admin/community"
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line text-xs"></i>
                Comunidad
              </Link>
              <div>
                <h2 className="text-foreground-950 font-bold text-base">Configuración de Comunidad</h2>
                <p className="text-foreground-400 text-xs">
                  Activá o desactivá las funciones comunitarias de tu radio
                </p>
              </div>
            </div>
            <Link
              to="/"
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer"
            >
              <i className="ri-external-link-line text-xs"></i>
              Ver sitio
            </Link>
          </div>

          {/* Content */}
          <div className="p-4 md:p-6 lg:p-8 pb-8">
            <div className="max-w-[720px] mx-auto">
              {/* Stats header */}
              <div className="bg-background-50 border border-background-200 rounded-2xl p-5 mb-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-accent-100 flex items-center justify-center">
                      <i className="ri-toggle-line text-accent-500 text-xl"></i>
                    </div>
                    <div>
                      <p className="text-foreground-950 font-bold text-base">
                        {activeCount} de {totalCount} funciones activas
                      </p>
                      <p className="text-foreground-400 text-[10px] mt-0.5">
                        {activeCount === totalCount
                          ? 'Todas las funciones de comunidad están habilitadas'
                          : activeCount === 0
                            ? 'No hay funciones de comunidad activas'
                            : 'Gestioná qué funciones querés ofrecer a tu audiencia'}
                      </p>
                    </div>
                  </div>
                  <div className="hidden sm:flex items-center gap-1.5">
                    <span className="w-16 h-1.5 rounded-full bg-background-200 overflow-hidden">
                      <span
                        className="block h-full rounded-full bg-accent-500 transition-all duration-500"
                        style={{ width: `${(activeCount / totalCount) * 100}%` }}
                      ></span>
                    </span>
                    <span className="text-foreground-400 text-[10px] font-medium">
                      {Math.round((activeCount / totalCount) * 100)}%
                    </span>
                  </div>
                </div>
              </div>

              {/* Features grid */}
              <div className="space-y-3 mb-6">
                {features.map((feature) => (
                  <div
                    key={feature.id}
                    className={`bg-background-50 border rounded-2xl p-5 transition-all ${
                      feature.enabled
                        ? 'border-background-200'
                        : 'border-background-200 opacity-65'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div
                        className={`w-11 h-11 rounded-xl ${feature.color} flex items-center justify-center shrink-0 ${
                          !feature.enabled && 'opacity-40'
                        }`}
                      >
                        <i className={`${feature.icon} text-background-50 text-lg`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <h3 className="text-foreground-900 font-semibold text-sm">{feature.label}</h3>
                          <button
                            onClick={() => toggleFeature(feature.id)}
                            className={`relative w-12 h-7 rounded-full transition-colors cursor-pointer shrink-0 ${
                              feature.enabled ? 'bg-accent-500' : 'bg-background-300'
                            }`}
                            aria-label={`${feature.enabled ? 'Desactivar' : 'Activar'} ${feature.label}`}
                          >
                            <span
                              className={`absolute top-0.5 w-6 h-6 rounded-full bg-background-50 shadow-sm transition-all ${
                                feature.enabled ? 'left-[22px]' : 'left-0.5'
                              }`}
                            ></span>
                          </button>
                        </div>
                        <p className="text-foreground-500 text-[11px] leading-relaxed mt-1.5">{feature.desc}</p>
                        <div className="flex items-center gap-2 mt-2.5">
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-semibold ${
                              feature.enabled
                                ? 'bg-accent-100 text-accent-700'
                                : 'bg-background-200 text-foreground-400'
                            }`}
                          >
                            <span
                              className={`w-1.5 h-1.5 rounded-full ${
                                feature.enabled ? 'bg-accent-500' : 'bg-background-400'
                              }`}
                            ></span>
                            {feature.enabled ? 'Activado' : 'Desactivado'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Save bar */}
              <div className="bg-background-50 border border-background-200 rounded-2xl p-5 flex items-center justify-between flex-wrap gap-3">
                <div>
                  <p className="text-foreground-800 text-xs font-semibold">
                    {activeCount} de {totalCount} funciones activas
                  </p>
                  <p className="text-foreground-400 text-[10px] mt-0.5">Los cambios se aplican al instante en el perfil público.</p>
                </div>
                <button
                  onClick={handleSave}
                  disabled={saveStatus === 'saving'}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-foreground-950 text-background-50 text-xs font-semibold hover:bg-foreground-800 transition-all cursor-pointer disabled:opacity-60 whitespace-nowrap"
                >
                  {saveStatus === 'saving' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-sm"></i>
                      Guardando...
                    </>
                  ) : (
                    <>
                      <i className="ri-check-line text-sm"></i>
                      Guardar Cambios
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>


    </div>
  );
}